/**
 * Environment variable validator
 * Fails fast if required environment variables are missing
 */

interface EnvConfig {
  name: string;
  required: boolean;
  validator?: (value: string) => boolean;
  errorMessage?: string;
}

const ENV_CONFIGS: EnvConfig[] = [
  {
    name: 'VITE_SUPABASE_URL',
    required: true,
    validator: (v) => v.startsWith('https://') && v.includes('.supabase.co'),
    errorMessage: 'Must be a valid Supabase URL',
  },
  {
    name: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    validator: (v) => v.length > 100,
    errorMessage: 'Must be a valid Supabase anon key',
  },
];

// Note: Analytics secret validation moved to server-side (edge function)
// Client should not reference secrets - they are validated in sanitized-analytics function

interface ValidationResult {
  valid: boolean;
  missing: string[];
  invalid: { name: string; error: string }[];
}

function resolveEnvValue(name: string): string | undefined {
  if (name === 'VITE_SUPABASE_ANON_KEY') {
    return import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_KEY;
  }

  return import.meta.env[name];
}

export function validateEnvironment(): ValidationResult {
  const missing: string[] = [];
  const invalid: { name: string; error: string }[] = [];

  for (const config of ENV_CONFIGS) {
    const value = resolveEnvValue(config.name);

    if (config.required && !value) {
      missing.push(config.name);
      continue;
    }

    if (value && config.validator && !config.validator(value)) {
      invalid.push({
        name: config.name,
        error: config.errorMessage || 'Invalid value',
      });
    }
  }

  return {
    valid: missing.length === 0 && invalid.length === 0,
    missing,
    invalid,
  };
}

export function assertEnvironment(): void {
  const result = validateEnvironment();

  if (!result.valid) {
    const errors: string[] = [];

    if (result.missing.length > 0) {
      errors.push(`Missing required environment variables: ${result.missing.join(', ')}`);
    }

    if (result.invalid.length > 0) {
      errors.push(
        `Invalid environment variables: ${result.invalid
          .map((i) => `${i.name} (${i.error})`)
          .join(', ')}`
      );
    }

    const message = `Environment validation failed:\n${errors.join('\n')}`;
    
    console.error(message);
    
    // In production, throw to prevent app from starting with invalid config
    if (import.meta.env.PROD) {
      throw new Error(message);
    }
  }
}

// Export for use in diagnostics
export function getEnvironmentStatus(): {
  configured: string[];
  missing: string[];
  mode: string;
} {
  const result = validateEnvironment();
  
  return {
    configured: ENV_CONFIGS
      .filter((c) => resolveEnvValue(c.name))
      .map((c) => c.name),
    missing: result.missing,
    mode: import.meta.env.MODE,
  };
}
